import { useCheckout } from '@stripe/react-stripe-js/checkout'

import { BAR_CHECKOUT_LOCALES, localeToCountryCode } from '@/types/locales'

import { excludeStripeCheckoutShippingFeeLineItems } from '@/lib/checkout/shippingFeeLineItems'
import type { CheckoutCountry, CurrencyCode } from '@/lib/checkout/types'
import { GUT_HEALTH_BAR_CONFIG } from '@/lib/gutHealthBar/utils'
import { isZoeTrial } from '@/lib/prices/priceUtils'
import { type StripePriceForTracking, priceRecurringIntervalToPeriod } from '@/lib/utils/getPricesTrackingData'

import { PriceProductType, type Price } from '@/clients/payment-service'

export interface OptionalItemSelection {
  priceLookupKey: string
  quantity: number
}

/**
 * Single update path for every upsell, binary or multi-SKU. Applies the desired
 * quantities onto the current basket (0 removes), leaving all other line items
 * untouched, and returns a fetch thunk for `checkout.runServerUpdate`.
 */
export type SetOptionalItems = (
  items: OptionalItemSelection[],
  currentSessionItems?: OptionalItemSelection[],
) => Promise<(() => Promise<Response>) | undefined>

export type UpsellItem = {
  lookupKey: string
  productName: string
  currency: string
  unitAmount: number
  discountedUnitAmount?: number
}

export type UpsellQuantityChange = { action: 'add' | 'remove'; priceLookupKey: string }

/**
 * Derive per-item add/remove intent for a set of desired upsell quantities. Each
 * item is compared against the quantity we last requested for it (`previousQuantities`),
 * falling back to the quantity already in the session (`sessionQuantities`). The
 * previous-request fallback lets the debounced bars stepper report one add/remove
 * per click even before the session has committed. Unchanged items yield nothing.
 *
 * Kept product-agnostic so any upsell — GHT, bars, future add-ons — is tracked
 * from the shared update path rather than each card wiring its own analytics.
 */
export const deriveUpsellQuantityChanges = (
  items: OptionalItemSelection[],
  previousQuantities: Record<string, number>,
  sessionQuantities: Record<string, number>,
): UpsellQuantityChange[] =>
  items.flatMap(({ priceLookupKey, quantity }): UpsellQuantityChange[] => {
    const previous = previousQuantities[priceLookupKey] ?? sessionQuantities[priceLookupKey] ?? 0
    if (quantity > previous) return [{ action: 'add', priceLookupKey }]
    if (quantity < previous) return [{ action: 'remove', priceLookupKey }]
    return []
  })

export const isBarsItem = (item: UpsellItem) => item.lookupKey.includes('BARS')

/**
 * Whether the California Proposition 65 warning must be shown. Keyed off the
 * delivery address, and checks both ingestibles flatly rather than as a
 * first-match branch, so a mixed basket still warns.
 */
export const shouldShowProp65Warning = (
  countryCode: CheckoutCountry,
  products: string[],
  shippingState: string | null,
): boolean =>
  countryCode === 'US' &&
  shippingState === 'CA' &&
  products.some((key) => key.includes('DAILY30') || key.includes('BARS'))

export const isBinaryOptionalItemAdded = (item: UpsellItem, lineItems: Array<{ name: string }> = []) =>
  lineItems.some((li) => li.name.toLowerCase() === item.productName.toLowerCase())

const isBarsLineItem = (name: string): boolean => /\bbars?\b/i.test(name)

export const formatBarsLineItemName = (name: string, quantity: number): string => `${quantity} x box of ${name}`

export const getLineItemDisplayName = (name: string, quantity: number): string => {
  if (isBarsLineItem(name)) return formatBarsLineItemName(name, quantity)
  return quantity > 1 ? `${quantity} × ${name}` : name
}

/**
 * Maps the live session's line items back to optional-item selections by
 * name-matching against the known upsell items (Stripe's checkout line items
 * don't expose a lookup key).
 */
export const getCurrentOptionalItems = (
  lineItems: Array<{ name: string; quantity: number }> = [],
  allOptionalItems: UpsellItem[] = [],
): OptionalItemSelection[] =>
  lineItems.flatMap((lineItem) => {
    const match = allOptionalItems.find((opt) => opt.productName.toLowerCase() === lineItem.name.toLowerCase())
    return match ? [{ priceLookupKey: match.lookupKey, quantity: lineItem.quantity }] : []
  })

/**
 * Unwrap the v6 useCheckout() discriminated union.
 * Returns the checkout value or null if loading/error.
 */
export const useCheckoutValue = () => {
  const state = useCheckout()
  if (state.type === 'success') return state.checkout
  return null
}

type CheckoutValue = NonNullable<ReturnType<typeof useCheckoutValue>>

export const hasDifferentFutureCharge = (checkout: CheckoutValue): boolean => {
  const lineItems = excludeStripeCheckoutShippingFeeLineItems(checkout.lineItems ?? [])
  const hasPaidOneOffItems = lineItems.some((item) => !item.recurring && item.total.minorUnitsAmount > 0)
  const hasOneTimeDiscount = checkout.discountAmounts?.some((discount) => !discount.recurring) ?? false

  return !!checkout.recurring?.dueNext && (hasPaidOneOffItems || !!checkout.recurring.trial || hasOneTimeDiscount)
}

/**
 * Returns a formatter function bound to the current checkout session's currency.
 * Use this instead of Stripe's pre-formatted `.amount` strings to avoid "US$" prefixes.
 */
export const useFormatAmount = () => {
  const checkout = useCheckoutValue()
  const currency = checkout?.currency ?? 'gbp'
  const divisor = checkout?.minorUnitsAmountDivisor ?? 100

  return (minorUnitsAmount: number) => formatCheckoutAmount(minorUnitsAmount, currency, divisor)
}

export const getUserType = (
  authenticatedUser: { metadata?: { creationTime?: string }; isAnonymous?: boolean } | null,
) => {
  const creationTime = authenticatedUser?.metadata?.creationTime
  if (creationTime && new Date(creationTime).getTime() < Date.now() - 60 * 60 * 1000) return 'Existing'
  return authenticatedUser?.isAnonymous ? 'Guest' : 'New'
}

export const buildTrackingItems = (
  lineItems: Array<{
    price: StripePriceForTracking
    amount_subtotal?: number
    amount_discount?: number
    // Present on combined complex-trial items — carries the paired recurring
    // price so item_category3 reflects the recurring period (e.g. "Yearly")
    // rather than the fee's "One off payment".
    dueNext?: { recurring: { interval: string; interval_count: number } | null }
  }>,
  itemBundle: string,
  countryCode: CheckoutCountry,
  getBasketKey?: (lookupKey: string) => string,
) =>
  lineItems.map((item) => ({
    item_bundle: itemBundle,
    item_id: getBasketKey ? getBasketKey(item.price.lookup_key || '') : item.price.lookup_key || '',
    item_name: item.price.nickname || '',
    item_category3: priceRecurringIntervalToPeriod(item.dueNext?.recurring ?? item.price.recurring),
    item_category4: countryCode,
    price: ((item.amount_subtotal || 0) - (item.amount_discount || 0)) / 100,
    quantity: 1,
  }))

const getSnackBarsUpsellFlavours = (countryCode: string) =>
  BAR_CHECKOUT_LOCALES.some((locale) => localeToCountryCode(locale) === countryCode)
    ? GUT_HEALTH_BAR_CONFIG.flavours.map((flavour) => ({
        lookupKey: `${countryCode}_${flavour}_12_BARS_WELCOME`,
        regularPriceLookupKey: `${countryCode}_${flavour}_12_BARS_ONE_TIME`,
      }))
    : []

export const getSnackBarsUpsellPriceLookupKeys = (countryCode: string): string[] =>
  getSnackBarsUpsellFlavours(countryCode).flatMap(({ lookupKey, regularPriceLookupKey }) => [
    lookupKey,
    regularPriceLookupKey,
  ])

const findPrice = (prices: Price[], lookupKey: string) => prices.find((price) => price.lookup_key === lookupKey)

// TODO(backend): temporary. Upsell eligibility is matched on lookup key strings
// because the basket has no price data here — it should be decided on the BE.
const MONTHLY_ZOE_PLUS_KEY_REGEX = /_(1_MONTH|MONTHLY)(_V\d+)?$/

export const getGhtUpsellItem = (
  countryCode: string,
  products: string[],
  prices: Price[] = [],
  isTreatment = false,
  sessionPrices: Price[] = [],
): UpsellItem | null => {
  if (countryCode !== 'GB') return null

  // Don't show upsell for trial purchases
  if (!isTreatment && sessionPrices.some(isZoeTrial)) return null

  // Show for any ZOE Plus product. Matching the product-type token mirrors how
  // payment-service resolves a product type from a lookup key.
  if (!products.some((p) => p.includes(PriceProductType.ZOE_PLUS))) return null

  // Don't show on monthly plans: the upsell is for commitment plans only.
  if (!isTreatment && products.some((p) => MONTHLY_ZOE_PLUS_KEY_REGEX.test(p))) return null

  // Don't show if GHT is already in the basket
  if (products.some((p) => p.includes('MICROBIOME'))) return null

  // No price, no card: better to drop the upsell than advertise an amount the
  // session won't charge.
  const price = prices.find((p) => p.product_type === PriceProductType.GUT_MICROBIOME_TEST)
  if (!price?.product_name) return null

  return {
    lookupKey: price.lookup_key,
    productName: price.product_name,
    currency: price.currency.toLowerCase(),
    unitAmount: price.unit_amount,
  }
}

export const getSnackBarsUpsellItems = (
  countryCode: string,
  products: string[],
  prices: Price[] = [],
  sessionPrices: Price[] = [],
): UpsellItem[] => {
  const flavours = getSnackBarsUpsellFlavours(countryCode)
  if (flavours.length === 0) return []
  if (sessionPrices.some(isZoeTrial)) return []
  if (!products.some((p) => p.includes('DAILY30'))) return []
  if (products.some((p) => p.includes('BARS'))) return []

  const items = flavours.flatMap(({ lookupKey, regularPriceLookupKey }) => {
    const discountedPrice = findPrice(prices, lookupKey)
    const regularPrice = findPrice(prices, regularPriceLookupKey)
    if (!discountedPrice?.product_name || !regularPrice) return []

    return [
      {
        lookupKey,
        productName: discountedPrice.product_name,
        currency: discountedPrice.currency.toLowerCase(),
        unitAmount: regularPrice.unit_amount,
        discountedUnitAmount: discountedPrice.unit_amount,
      },
    ]
  })

  // The card prices every flavour off the first item, so a partial price book
  // would misprice the rest — show all flavours or none.
  return items.length === flavours.length ? items : []
}

/**
 * Format a Stripe checkout amount using the session currency.
 * Uses the locale matching the currency to avoid "US$" prefixes
 * (e.g. USD → en-US → "$220.00", GBP → en-GB → "£220.00").
 */
const CURRENCY_LOCALES: Record<string, string> = {
  usd: 'en-US',
  gbp: 'en-GB',
  aud: 'en-AU',
  nzd: 'en-NZ',
} satisfies Record<CurrencyCode, string>

export const formatCheckoutAmount = (minorUnitsAmount: number, currency: string, divisor: number = 100) => {
  const amount = minorUnitsAmount / divisor
  const locale = CURRENCY_LOCALES[currency.toLowerCase()] || 'en'
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`
  }
}

/**
 * Format a Stripe unit amount (in minor units, e.g. pence/cents) as a
 * localised currency string.
 */
export const formatStripeUnitAmount = (unitAmount: number, currency: string) => {
  const amount = unitAmount / 100
  try {
    // stripIfInteger: £149 rather than £149.00, but £149.50 keeps its pence.
    return new Intl.NumberFormat(CURRENCY_LOCALES[currency.toLowerCase()] || 'en', {
      style: 'currency',
      currency,
      trailingZeroDisplay: 'stripIfInteger',
    }).format(amount)
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`
  }
}

/** Billing terms for a line item, e.g. "Billed monthly" or "One time purchase". */
export const getBillingLabel = (
  recurring: { interval: string; intervalCount: number } | null | undefined,
  hasTrial: boolean,
): string => {
  if (!recurring) return 'One time purchase'
  const { interval, intervalCount } = recurring
  const prefix = hasTrial ? 'Prorated, then billed' : 'Billed'
  if (intervalCount === 1) {
    const adverb =
      interval === 'year'
        ? 'annually'
        : interval === 'month'
          ? 'monthly'
          : interval === 'week'
            ? 'weekly'
            : interval === 'day'
              ? 'daily'
              : `every ${interval}`
    return `${prefix} ${adverb}`
  }
  return `${prefix} every ${intervalCount} ${interval}s`
}

/** How often a recurring charge repeats, e.g. "per month". */
export const getFrequencyLabel = (interval: string, intervalCount: number): string => {
  if (intervalCount > 1) return `every ${intervalCount} ${interval}s`
  return `per ${interval}`
}

/**
 * The renewal line shown under a complex-trial fee item, e.g.
 * "Then auto-renewal to £133.00. Billed every 4 months, starting 26 August 2026".
 * The starting date is only appended when the basket has a trial.
 */
export const getTrialFeeSubtitle = (recurring: {
  interval: string
  intervalCount: number
  dueNext: { total: { amount: string } }
  trial?: { trialEnd: number } | null
}): string => {
  const frequency = getFrequencyLabel(recurring.interval, recurring.intervalCount)
  const startingClause = recurring.trial
    ? `, starting ${new Date(recurring.trial.trialEnd * 1000).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })}`
    : ''
  return `Then auto-renewal to ${recurring.dueNext.total.amount}. Billed ${frequency}${startingClause}`
}
